import { cloneDeep, debounce } from "lodash";
import { useState, useRef } from "react";
import { useNavigate } from 'react-router-dom';

import { handleSetOtherValuePaths } from "./general";
import { setVal } from "oolib";
import { useGetQueryData } from "./react-query-hooks/general";
import { formValidationCheckV2 } from "./validation/formValidation";
import { useCreateContent } from "../pageTpls/ContributeTpl/queryHooks/useCreateContent";
import { useCreateAndPublishContent } from "./react-query-hooks/contributions";
import { mergeAnnoDataIntoAnnotationsTags, getRoutePathToEditContent, getRoutePathToPublishedContent, blockRegistry } from "@okf/ootils";
import { isContentCollabEnabled } from "./collab/isContentCollabEnabled";

export const useBeginPublishingFlow = () => {
  const navigate = useNavigate();
  const createContent = useCreateContent();
  const createAndPublishContent = useCreateAndPublishContent();
  const platformConfigs = useGetQueryData("platformConfigs") || {};
  const { SELF_MANAGED_BASE_CONFIGS } = platformConfigs;
  // collab tenants have no draft lifecycle: the legacy clone/moderation routes
  // are server-gated (rejectLegacyLifecycleOnCollabTenants), so docs are born
  // published and edited in place on the published page
  const collabModeEnabled = isContentCollabEnabled(platformConfigs);

  const baseContent = (contentType, presetValues = []) => {
    let base = {
      main: {},
      meta: {
        kp_content_type: contentType,
        // kp_content_tpl: '123'
      }
    }

    presetValues.map(p => {
      base = setVal(base, p.valuePath, p.value)
    })
    return base;
  };

  const [generatingContentType, setGeneratingContentType] = useState(undefined);

  const generateFn = (contentTypeConfig, options = {}, presetValues = []) => {
    if (contentTypeConfig.kp_settings?.[0]?.blocks?.length > 0) {
      navigate(`/contribute-config/${contentTypeConfig.kp_content_type}`);
    } else if (collabModeEnabled) {
      setGeneratingContentType(contentTypeConfig.kp_content_type);
      createAndPublishContent.mutate(
        {
          contentType: contentTypeConfig.kp_content_type,
          content: baseContent(contentTypeConfig.kp_content_type, presetValues),
        },
        {
          onSuccess: (res) => {
            options.onSuccess
              ? options.onSuccess(res)
              : navigate(
                  getRoutePathToPublishedContent({
                    contentType: res.data.content.meta.kp_content_type,
                    contentId: res.data.content._id,
                    SELF_MANAGED_BASE_CONFIGS,
                  })
                );
          },
        }
      );
    } else {
      setGeneratingContentType(contentTypeConfig.kp_content_type);
      createContent.mutate(
        {
          contentType: contentTypeConfig.kp_content_type,
          content: baseContent(contentTypeConfig.kp_content_type, presetValues),
          ...(options.updateSelfServeSurvey ? {updateSelfServeSurvey: options.updateSelfServeSurvey} : {})
        },
        {
          onSuccess: (res) => {
            options.onSuccess
              ? options.onSuccess(res)
              : navigate(
                  getRoutePathToEditContent({
                    contentType: res.data.content.meta.kp_content_type,
                    contentId: res.data.content._id,
                    SELF_MANAGED_BASE_CONFIGS,
                  })
                );
          },
        }
      );
    }
  };

  return {
    generateFn,
    thisTemplateIsGenerating: (d) =>
      d.kp_content_type === generatingContentType &&
      (createContent.isLoading || createAndPublishContent.isLoading), // this content type check if relevant on choose content type page
    generateTplIsLoading: createContent.isLoading || createAndPublishContent.isLoading, // a general boolean that tells if createContent is running or no
  };
};

export const useHandleContentChange = (props = {}) => {
  const debounceMap = useRef(new Map());
  const { setHasPendingChanges } = props;
  const { user } = useGetQueryData('userData') || {};
  
  /**
   * extremely critical to have debounce per valuepath, else,
   * a global debounce could end up ignoring an onchange from a
   * seprate valuepath, and that would end up completely ignoring 
   * the update to that value path, leading to data loss.
   */
  const getDebounceForPath = (valuePath) => {
    if (!debounceMap.current.has(valuePath)) {
      debounceMap.current.set(
        valuePath,
        debounce((fn) => fn(), 1000)
      );
    }
    return debounceMap.current.get(valuePath);
  };

  // const flushOtherPendingDebounces = (currentValuePath) => {
  //   debounceMap.current.forEach((debouncedFn, path) => {
  //     // Flush all OTHER paths (not the current one)
  //     if (path !== currentValuePath && debouncedFn.flush) {
  //       debouncedFn.flush();
  //     }
  //   });
  // };

  const handleContentChange = ({
    block,
    value,
    setFormValidation,
    blocksToValidate,
    setContent,
    mutate, //optional
    optimizeFieldUpdatesInMutation,
    options = {}
    //these are passed via the options argument
  }) => {
    const { valuePath, setValuePath, setValuePathFn, setValuePathArgs } = block;
    const { mergeAnnoArgsFromRepeater } = options

    if (setHasPendingChanges) setHasPendingChanges(true);
    
    setContent((prev) => {
      let newC = cloneDeep(prev);
      newC = setVal(newC, valuePath, value);

      /** OPTIMIZE:  run this only if this block is found in failed blocks */
      const { failedBlocks } = formValidationCheckV2({
        blocks: blocksToValidate,
        content: newC,
      });
      // an emptied field can't be "invalid input" — clear it so optional fields don't stay stuck after being cleared
      setFormValidation && setFormValidation((p) => ({ ...p, failedBlocks, ...((value ?? '') === '' && { invalidInputBlocks: (p.invalidInputBlocks || []).filter(b => b.valuePath !== valuePath) }) }));
      // Annotation enabled via registry capability (instance-aware — the block is passed
      // so props.annotations.disable opts the field out), or via the repeater context
      // (mergeAnnoArgsFromRepeater is itself only set when the repeater child is enabled).
      const annotationsFieldMustBeUpdated = (
        blockRegistry.hasCapability(block.comp, 'annotation', block) ||
        mergeAnnoArgsFromRepeater
      );
      if (
        annotationsFieldMustBeUpdated
        /**
         * in the case of a regular block we check to see if annotation is enabled on it.
         * in the case that the update is coming from a block inside the repeater, if
         * mergeAnnoArgsFromRepeater exists, then that essentially means annotation is enabled
         * on that child block of the repeater.
         */
      ) {
        // Lexical-shape annotation merge. (DraftJS KPRichInput / RichTextEditor
        // annotation has been removed — only Lexical annotation merges here now.)
        const mergeAnnoArgs = mergeAnnoArgsFromRepeater
          ? {
              prevObj: newC.annotations?.tags,
              newLexValue: mergeAnnoArgsFromRepeater.newLexValue,
              thisBlockValuePath: `${block.valuePath}.${mergeAnnoArgsFromRepeater.idxAndValuePath}`,
              author: user ? { id: user._id, name: user.name } : undefined,
            }
          : {
              prevObj: newC.annotations?.tags,
              newLexValue: value,
              thisBlockValuePath: block.valuePath,
              author: user ? { id: user._id, name: user.name } : undefined,
            };
        newC.annotations = {
          ...(newC.annotations || {}),
          tags: mergeAnnoDataIntoAnnotationsTags(mergeAnnoArgs),
        };
      }
      let setOtherValuePathsOutput = {};
      if (setValuePath) {
        setOtherValuePathsOutput = handleSetOtherValuePaths({
          setValuePath,
          setValuePathFn,
          setValuePathArgs,
          value,
          content: newC,
        });
      }

      const updateFields = {
        $set: {
          ...(value !== undefined ? {[valuePath]: value} : {}),
          ...(annotationsFieldMustBeUpdated ? {annotations: newC.annotations} : {}),
          ...setOtherValuePathsOutput
        },
        ...(value === undefined ? {$unset: {[valuePath]: ''}} : {})
      }



      if (mutate) {
        // // Flush any pending updates from OTHER fields before starting this one
        // flushOtherPendingDebounces(valuePath);
        
        // Get the debounce function specific to this valuePath
        const debouncedFn = getDebounceForPath(valuePath);
        
        debouncedFn(() => {
          mutate(
            optimizeFieldUpdatesInMutation
            ? {
                updateFields
              }
            : { // most likely never used
                updatedContent: newC 
              }
          );

          if (setHasPendingChanges) {
            setHasPendingChanges(false);
          }
        });
      }

      return newC;
    });
  };

  return { handleContentChange };
};
